/**
 * Centralized logging utility for the application
 * Provides different log levels and can be configured for production vs development
 */

// Log levels
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// Current log level (can be configured based on environment)
const CURRENT_LOG_LEVEL = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

/**
 * Logger class with different log levels
 */
class Logger {
  constructor(context = 'App') {
    this.context = context;
  }

  /**
   * Creates a logger with specific context
   * @param {string} context - Context name for the logger
   * @returns {Logger} - Logger instance
   */
  static create(context) {
    return new Logger(context);
  }

  /**
   * Formats log message with timestamp and context
   * @param {string} level - Log level
   * @param {string} message - Log message
   * @param {any} data - Additional data to log
   * @returns {string} - Formatted message
   */
  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const baseMessage = `[${timestamp}] [${level}] [${this.context}] ${message}`;
    
    if (data) {
      return `${baseMessage}\nData:`;
    }
    return baseMessage;
  }

  /**
   * Logs error messages
   * @param {string} message - Error message
   * @param {any} data - Additional error data
   */
  error(message, data = null) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.ERROR) {
      console.error(this.formatMessage('ERROR', message), data || '');
    }
  }

  /**
   * Logs warning messages
   * @param {string} message - Warning message
   * @param {any} data - Additional warning data
   */
  warn(message, data = null) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.WARN) {
      console.warn(this.formatMessage('WARN', message), data || '');
    }
  }

  /**
   * Logs info messages
   * @param {string} message - Info message
   * @param {any} data - Additional info data
   */
  info(message, data = null) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.INFO) {
      console.info(this.formatMessage('INFO', message), data || '');
    }
  }

  /**
   * Logs debug messages
   * @param {string} message - Debug message
   * @param {any} data - Additional debug data
   */
  debug(message, data = null) {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.DEBUG) {
      console.log(this.formatMessage('DEBUG', message), data || '');
    }
  }

  /**
   * Logs file upload related events
   * @param {string} event - Upload event type
   * @param {string} filename - File name
   * @param {any} details - Additional details
   */
  uploadEvent(event, filename, details = null) {
    this.info(`File Upload ${event}: ${filename}`, details);
  }

  /**
   * Logs API call events
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @param {any} data - Request/response data
   */
  apiCall(method, url, data = null) {
    this.debug(`API Call: ${method} ${url}`, data);
  }

  /**
   * Logs performance metrics
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in milliseconds
   * @param {any} metadata - Additional metadata
   */
  performance(operation, duration, metadata = null) {
    this.info(`Performance: ${operation} took ${duration}ms`, metadata);
  }
}

// Create default logger instance
const logger = new Logger('Default');

// Add create method to default logger instance
logger.create = Logger.create;

// Export both the class and default instance
export { Logger, LOG_LEVELS };
export default logger;
